/**
 * Copyright Highway9 Networks Inc.
 */
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { AreaChartSeries, UtilizationData } from "@h9/ui-lib";
import { RootState } from "..";
import { deploymentCloudServices } from "~/services/deploymentCloud-service";
import { serviceInstanceService } from "~/services/serviceInstance-service";
import {
  buildIpsecMetricsRequest,
  filterIpsecVmcAllocationsByActiveDeploymentCloud,
  isIpsecVmcAllocationArray,
  mapIpsecVmcAllocationsToRows,
  normalizeEdgeMetricsAggregateData,
  PoolTimeRange,
  snapshotToPoolUtilization,
  timeSeriesToPoolUtilizationChartSeries,
  VmcAllocationRow,
} from "~/views/Administration/IpsecCertificates/metricsUtils";

async function fetchEdgeMetricsAggregate(timeRange: PoolTimeRange) {
  const payload = buildIpsecMetricsRequest(timeRange);
  const data = await serviceInstanceService.getMetrics(payload);
  const normalizedData = normalizeEdgeMetricsAggregateData(data);
  if (!normalizedData) {
    throw new Error("Unexpected edge metrics response format");
  }
  return normalizedData;
}

async function fetchVmcAllocationsFromApi() {
  const [data, deploymentClouds] = await Promise.all([
    serviceInstanceService.getIpsecVmcAllocations(),
    deploymentCloudServices.getClusterAgents(),
  ]);
  if (!isIpsecVmcAllocationArray(data)) {
    throw new Error("Unexpected serviceInstances allocation response format");
  }
  if (!Array.isArray(deploymentClouds)) {
    throw new Error("Unexpected deploymentClouds response format");
  }
  return filterIpsecVmcAllocationsByActiveDeploymentCloud(data, deploymentClouds);
}

interface IpsecCertMetricsState {
  timeRange: PoolTimeRange;
  poolStats: UtilizationData | null;
  chartSeries: AreaChartSeries[];
  allocations: VmcAllocationRow[];
  loading: boolean;
  allocationsLoading: boolean;
  error: string | null;
  allocationsError: string | null;
}

const initialState: IpsecCertMetricsState = {
  timeRange: "24h",
  poolStats: null,
  chartSeries: [],
  allocations: [],
  loading: false,
  allocationsLoading: false,
  error: null,
  allocationsError: null,
};

export const fetchIpsecCertMetrics = createAsyncThunk(
  "ipsecCertMetrics/fetch",
  async (timeRange: PoolTimeRange, { rejectWithValue }) => {
    try {
      const response = await fetchEdgeMetricsAggregate(timeRange);

      return {
        timeRange,
        poolStats: snapshotToPoolUtilization(response.snapshot),
        chartSeries: timeSeriesToPoolUtilizationChartSeries(response.timeSeries),
      };
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : "Failed to fetch IPSEC cert pool metrics";
      return rejectWithValue(message);
    }
  }
);

export const fetchIpsecVmcAllocations = createAsyncThunk(
  "ipsecCertMetrics/fetchAllocations",
  async (_, { rejectWithValue }) => {
    try {
      const allocations = await fetchVmcAllocationsFromApi();
      return mapIpsecVmcAllocationsToRows(allocations);
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : "Failed to fetch IPSEC VMC allocations";
      return rejectWithValue(message);
    }
  }
);

const ipsecCertMetricsSlice = createSlice({
  name: "ipsecCertMetrics",
  initialState,
  reducers: {
    setPoolTimeRange: (state, action: PayloadAction<PoolTimeRange>) => {
      state.timeRange = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearAllocationsError: (state) => {
      state.allocationsError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchIpsecCertMetrics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchIpsecCertMetrics.fulfilled, (state, action) => {
        state.loading = false;
        state.timeRange = action.payload.timeRange;
        state.poolStats = action.payload.poolStats;
        state.chartSeries = action.payload.chartSeries;
      })
      .addCase(fetchIpsecCertMetrics.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) ?? action.error.message ?? "Request failed";
      })
      .addCase(fetchIpsecVmcAllocations.pending, (state) => {
        state.allocationsLoading = true;
        state.allocationsError = null;
      })
      .addCase(fetchIpsecVmcAllocations.fulfilled, (state, action) => {
        state.allocationsLoading = false;
        state.allocations = action.payload;
      })
      .addCase(fetchIpsecVmcAllocations.rejected, (state, action) => {
        state.allocationsLoading = false;
        state.allocationsError =
          (action.payload as string) ?? action.error.message ?? "Request failed";
      });
  },
});

export const ipsecCertMetricsActions = ipsecCertMetricsSlice.actions;
export default ipsecCertMetricsSlice.reducer;

export const selectIpsecCertPoolTimeRange = (state: RootState) => state.ipsecCertMetrics.timeRange;
export const selectIpsecCertPoolStats = (state: RootState) => state.ipsecCertMetrics.poolStats;
export const selectIpsecCertPoolChartSeries = (state: RootState) => state.ipsecCertMetrics.chartSeries;
export const selectIpsecVmcAllocations = (state: RootState) => state.ipsecCertMetrics.allocations;
export const selectIpsecCertMetricsLoading = (state: RootState) => state.ipsecCertMetrics.loading;
export const selectIpsecVmcAllocationsLoading = (state: RootState) =>
  state.ipsecCertMetrics.allocationsLoading;
export const selectIpsecCertMetricsError = (state: RootState) => state.ipsecCertMetrics.error;
export const selectIpsecVmcAllocationsError = (state: RootState) =>
  state.ipsecCertMetrics.allocationsError;
